// src/components/Countries/NewCountryModal.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import { createCountry } from "../../redux/slices/countriesSlice";
import { showNotification } from "../../redux/slices/notificationSlice";
import { useTranslation } from "../../contexts/LanguageContext";

const NewCountryModal = ({ isOpen, setIsOpen }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  // List of countries fetched from the REST Countries API
  const [countriesList, setCountriesList] = useState([]);

  // Form data state; fields that can be auto-inserted are initialized as empty
  const [formData, setFormData] = useState({
    countryFlagImage: "",
    countryCode: "",
    countryName: "",
    currency: "",
    localCurrencySymbol: "",
    globalCurrencySymbol: "",
    defaultLanguage: "",
    taxRate: "", // must be entered manually (converted to a number)
    timezone: "",
    mobileCountryCode: "",
    mobileDigitLimit: "", // number of digits in mobile number
    isActive: true,
    createdBy: "",
    updatedBy: ""
  });

  // Fetch countries list on mount
  useEffect(() => {
    const fetchCountriesList = async () => {
      try {
        // API now requires fields parameter - specifying all needed fields
        const response = await axios.get(
          "https://restcountries.com/v3.1/all?fields=name,cca2,flags,currencies,languages,timezones,idd",
          {
            timeout: 10000,
          }
        );
        // Sort countries alphabetically by common name
        const sortedCountries = response.data.sort((a, b) =>
          a.name.common.localeCompare(b.name.common)
        );
        setCountriesList(sortedCountries);
        console.log(`Successfully loaded ${sortedCountries.length} countries`);
      } catch (error) {
        console.error("Error fetching countries list:", error.message);
        dispatch(
          showNotification({
            message: t("country.msg.failedLoadListRetry"),
            type: "error"
          })
        );
      }
    };
    if (isOpen) {
      fetchCountriesList();
    }
  }, [isOpen, dispatch, t]);

  // Adjusted handleChange to convert numeric fields accordingly
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : name === "taxRate"
          ? parseFloat(value)
          : name === "mobileDigitLimit"
          ? parseInt(value, 10)
          : value
    }));
  };

  // When a country is selected, auto-fill as many fields as possible
  const handleCountrySelect = (e) => {
    const selectedCountryCode = e.target.value;
    const selectedCountry = countriesList.find(
      (c) => c.cca2 === selectedCountryCode
    );
    if (selectedCountry) {
      // Auto-filled values from the API:
      const flagImage = selectedCountry.flags?.png || "";
      const countryCode = selectedCountry.cca2 || "";
      const countryName = selectedCountry.name?.common || "";

      // For currencies, take the first currency if available.
      let currency = "";
      let localCurrencySymbol = "";
      let globalCurrencySymbol = "";
      if (selectedCountry.currencies) {
        const currencyKeys = Object.keys(selectedCountry.currencies);
        if (currencyKeys.length > 0) {
          currency = currencyKeys[0]; // e.g. "KWD" or "PKR"
          localCurrencySymbol =
            selectedCountry.currencies[currency]?.symbol || "";
          // Set globalCurrencySymbol to the currency code by default.
          globalCurrencySymbol = currency;
        }
      }

      // For language, pick the first language if available.
      let defaultLanguage = "";
      if (selectedCountry.languages) {
        const languageKeys = Object.keys(selectedCountry.languages);
        if (languageKeys.length > 0) {
          // Use the language value (e.g. "Arabic" or "English")
          defaultLanguage = selectedCountry.languages[languageKeys[0]];
        }
      }

      // Timezone: take the first timezone from the array.
      const timezone = selectedCountry.timezones?.[0] || "";

      // Mobile country code: combine idd.root and the first suffix if available.
      let mobileCountryCode = "";
      if (selectedCountry.idd) {
        mobileCountryCode =
          selectedCountry.idd.root +
          (selectedCountry.idd.suffixes &&
          selectedCountry.idd.suffixes.length > 0
            ? selectedCountry.idd.suffixes[0]
            : "");
      }

      setFormData((prev) => ({
        ...prev,
        countryCode,
        countryName,
        countryFlagImage: flagImage,
        currency,
        localCurrencySymbol,
        globalCurrencySymbol,
        defaultLanguage,
        timezone,
        mobileCountryCode
      }));
    }
  };

  // Handle form submission: dispatch the createCountry action
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(createCountry(formData)).unwrap();
      dispatch(
        showNotification({
          message: t("country.msg.createdSuccess"),
          type: "success"
        })
      );
      setIsOpen(false);
    } catch (error) {
      dispatch(
        showNotification({
          message: error || t("country.msg.failedCreate"),
          type: "error"
        })
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white p-6 rounded-md w-full max-w-lg">
        <h2 className="text-xl font-bold mb-4">{t("country.modal.add")}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Country selection dropdown */}
          <div>
            <label className="block mb-1 font-medium">{t("country.field.selectCountry")}</label>
            <select
              onChange={handleCountrySelect}
              value={formData.countryCode}
              className="w-full border px-3 py-2 rounded-md"
            >
              <option value="">{t("country.field.selectPlaceholder")}</option>
              {countriesList.map((country) => (
                <option key={country.cca2} value={country.cca2}>
                  {country.name.common}
                </option>
              ))}
            </select>
          </div>

          {/* Display the selected flag and name */}
          {formData.countryFlagImage && (
            <div className="flex items-center space-x-2">
              <img
                src={formData.countryFlagImage}
                alt={formData.countryName}
                className="h-10 w-auto"
              />
              <span>{formData.countryName}</span>
            </div>
          )}

          {/* Auto-filled mobile country code display */}
          {formData.mobileCountryCode && (
            <div className="flex items-center space-x-2">
              <span className="font-medium">{t("country.field.mobileCode")}</span>
              <span>{formData.mobileCountryCode}</span>
            </div>
          )}

          {/* Other form fields to be filled manually */}
          <input
            type="text"
            name="currency"
            placeholder={t("country.field.currency")}
            value={formData.currency}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded-md"
            required
          />
          <input
            type="text"
            name="localCurrencySymbol"
            placeholder={t("country.field.localSymbol")}
            value={formData.localCurrencySymbol}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded-md"
          />
          <input
            type="text"
            name="globalCurrencySymbol"
            placeholder={t("country.field.globalSymbol")}
            value={formData.globalCurrencySymbol}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded-md"
          />
          <input
            type="text"
            name="defaultLanguage"
            placeholder={t("country.field.defaultLanguage")}
            value={formData.defaultLanguage}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded-md"
            required
          />
          <input
            type="number"
            step="0.01"
            name="taxRate"
            placeholder={t("country.field.taxRate")}
            value={formData.taxRate}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded-md"
            required
          />
          <input
            type="text"
            name="timezone"
            placeholder={t("country.field.timezone")}
            value={formData.timezone}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded-md"
            required
          />
          <input
            type="text"
            name="mobileCountryCode"
            placeholder={t("country.field.mobileCountryCode")}
            value={formData.mobileCountryCode}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded-md"
            required
          />
          <input
            type="number"
            name="mobileDigitLimit"
            placeholder={t("country.field.mobileDigitLimit")}
            value={formData.mobileDigitLimit}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded-md"
            required
            min="1"
            max="20"
          />
          <div className="flex items-center">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
              className="mr-2"
            />
            <label>{t("country.field.active")}</label>
          </div>
          <div className="mt-4 flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="px-4 py-2 border rounded-md"
            >
              {t("country.button.cancel")}
            </button>
            <button
              type="submit"
              className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-md"
            >
              {t("country.button.create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewCountryModal;
